// @ts-nocheck — ported helpers from operators_dashboards EnrollmentDashboard.jsx
import { useEffect, useState } from "react";

export const asPct = (value) => `${Math.round((value ?? 0) * 100)}%`;
export const fmtN = (value) => (value == null ? "—" : Number(value).toLocaleString());

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(timeout);
  }, [delayMs, value]);
  return debouncedValue;
}

export const COLORS = {
  blue: "#0081AF",
  orange: "#F7941D",
  green: "#2A9D8F",
  red: "#C84C35",
  gray: "#7a7467",
  lightGray: "#d1d5db",
  purple: "#7C3AED",
  yellow: "#D6A400",
  lowNGray: "#9ca3af",
};

export const CALL_REVIEW_MAX_RANGE_DAYS = 92;

export const SNAPSHOT_MARKETABLE_METRICS = [
  ["marketablePatients", "Marketable patients excluding enrolled", COLORS.green],
];

export function todayIso() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
  }).format(new Date());
}

export function recentAnalysisDates(anchorDate = todayIso(), count = 10) {
  return Array.from({ length: count }, (_, index) =>
    shiftIsoDate(anchorDate, index - count + 1, "day"),
  );
}

export function analysisDateLabel(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(dateIso + "T00:00:00Z");
  if (Number.isNaN(date.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  const month = part("month");
  return [part("weekday"), month === "Sep" ? "Sept" : month, part("day")].join(" ");
}

export function shortDateLabel(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function weekRangeLabel(weekStart, weekEnd) {
  return `${shortDateLabel(weekStart)}–${shortDateLabel(weekEnd)}`;
}

export function quantile(values, percentile) {
  if (!values.length) return 0;
  const sorted = [...values].sort((left, right) => left - right);
  const position = (sorted.length - 1) * percentile;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  if (lowerIndex === upperIndex) return sorted[lowerIndex];
  const weight = position - lowerIndex;
  return sorted[lowerIndex] * (1 - weight) + sorted[upperIndex] * weight;
}

export function esConversionQuartiles(rows) {
  const rates = rows
    .filter((row) => row.eligibleStartedCalls > 0)
    .map((row) => row.conversionRate ?? 0);
  return [quantile(rates, 0.25), quantile(rates, 0.5), quantile(rates, 0.75)];
}

export function groupEsConversionWeeks(rows) {
  const weeks = new Map();
  rows.forEach((row) => {
    const existing = weeks.get(row.weekStart) || {
      weekStart: row.weekStart,
      weekEnd: row.weekEnd,
      rows: [],
    };
    existing.rows.push(row);
    weeks.set(row.weekStart, existing);
  });
  return [...weeks.values()]
    .sort((left, right) => left.weekStart.localeCompare(right.weekStart))
    .map((week) => ({
      ...week,
      rows: [...week.rows].sort((left, right) => {
        const rateDifference = (right.conversionRate ?? -1) - (left.conversionRate ?? -1);
        if (rateDifference !== 0) return rateDifference;
        return left.esName.localeCompare(right.esName);
      }),
    }));
}

export function summarizeEsConversion(rows) {
  const weeks = [
    ...new Map(
      rows.map((row) => [
        row.weekStart,
        {
          weekStart: row.weekStart,
          weekEnd: row.weekEnd,
        },
      ]),
    ).values(),
  ].sort((left, right) => left.weekStart.localeCompare(right.weekStart));
  const specialists = new Map();

  rows.forEach((row) => {
    const specialist = specialists.get(row.esName) || {
      esName: row.esName,
      eligibleStartedCalls: 0,
      enrolled: 0,
      weeks: new Map(),
    };
    const week = specialist.weeks.get(row.weekStart) || {
      eligibleStartedCalls: 0,
      enrolled: 0,
    };
    week.eligibleStartedCalls += row.eligibleStartedCalls;
    week.enrolled += row.enrolled;
    specialist.eligibleStartedCalls += row.eligibleStartedCalls;
    specialist.enrolled += row.enrolled;
    specialist.weeks.set(row.weekStart, week);
    specialists.set(row.esName, specialist);
  });

  const summaries = [...specialists.values()].map((specialist) => {
    const weeklyRates = weeks.map(({ weekStart }) => {
      const week = specialist.weeks.get(weekStart) || {
        eligibleStartedCalls: 0,
        enrolled: 0,
      };
      return {
        ...week,
        conversionRate: week.eligibleStartedCalls
          ? week.enrolled / week.eligibleStartedCalls
          : null,
      };
    });
    const ratesWithData = weeklyRates
      .map((week) => week.conversionRate)
      .filter((rate) => rate != null);

    return {
      ...specialist,
      conversionRate: specialist.eligibleStartedCalls
        ? specialist.enrolled / specialist.eligibleStartedCalls
        : null,
      weeklyRates,
      medianConversion: ratesWithData.length ? quantile(ratesWithData, 0.5) : null,
      minConversion: ratesWithData.length ? Math.min(...ratesWithData) : null,
      maxConversion: ratesWithData.length ? Math.max(...ratesWithData) : null,
    };
  });

  summaries.sort((left, right) => {
    const rateDifference = (right.conversionRate ?? -1) - (left.conversionRate ?? -1);
    if (rateDifference !== 0) return rateDifference;
    return left.esName.localeCompare(right.esName);
  });

  return { weeks, summaries };
}

export function shortWeekdayDateLabel(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function snapshotHistoryDateLabel(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatEtDateTime(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/New_York",
  }).format(date);
}

export function formatEtTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(date);
}

export function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return "—";
  const rounded = Math.max(0, Math.round(Number(seconds)));
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function monthLabel(dateIso) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

export function monthEndIso(year, monthIndex) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).toISOString().slice(0, 10);
}

export function isEndOfMonth(dateIso) {
  const [year, month, day] = dateIso.split("-").map(Number);
  return day === new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function monthStartIso(dateIso) {
  const [year, month] = dateIso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 10);
}

export function shiftIsoDate(dateIso, amount, unit) {
  const [year, month, day] = dateIso.split("-").map(Number);
  if (unit === "month") {
    const targetMonth = month - 1 + amount;
    const monthStart = new Date(Date.UTC(year, targetMonth, 1));
    const monthEnd = new Date(Date.UTC(year, targetMonth + 1, 0));
    const clampedDay = isEndOfMonth(dateIso)
      ? monthEnd.getUTCDate()
      : Math.min(day, monthEnd.getUTCDate());
    monthStart.setUTCDate(clampedDay);
    return monthStart.toISOString().slice(0, 10);
  }

  const shifted = new Date(Date.UTC(year, month - 1, day));
  if (unit === "day") shifted.setUTCDate(shifted.getUTCDate() + amount);
  if (unit === "week") shifted.setUTCDate(shifted.getUTCDate() + amount * 7);
  return shifted.toISOString().slice(0, 10);
}

export function isoDatesInclusive(start, end) {
  const dates = [];
  for (let date = start; date <= end; date = shiftIsoDate(date, 1, "day")) {
    if (dates.length >= CALL_REVIEW_MAX_RANGE_DAYS) {
      throw new Error(`Date range cannot exceed ${CALL_REVIEW_MAX_RANGE_DAYS} days.`);
    }
    dates.push(date);
  }
  return dates;
}

export function comparisonConfig(activeTab) {
  if (activeTab === "monthly") return { unit: "month", label: "prior month" };
  return { unit: "day", label: "prior day" };
}

export function formatPctChange(currentValue, previousValue) {
  const current = Number(currentValue || 0);
  const previous = Number(previousValue || 0);
  if (previous === 0) return current === 0 ? "0%" : "—";
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.round(Math.abs(pct));
  const sign = pct > 0 ? "+" : pct < 0 ? "-" : "";
  return `${sign}${rounded}%`;
}

export function safeRate(numerator, denominator) {
  if (!denominator || denominator <= 0) return 0;
  return numerator / denominator;
}

export function summarizeCallMetrics(period = {}) {
  const totalBookings = Number(period.totalBookings || 0);
  const remainingCalls = Number(period.remainingCalls ?? period.nonCancelledCalls ?? 0);
  const noShowCalls = Number(period.noShowCalls || 0);
  const notEligibleCalls = Number(period.notEligibleCalls || 0);
  const lastMinuteRescheduleCalls = Number(period.lastMinuteRescheduleCalls || 0);
  const convertiblePatients = Number(period.convertibleCalls || 0);
  const enrolledCalls = Number(period.enrolledCalls || 0);
  const pendingReferralCalls = Number(period.pendingReferralCalls || 0);
  const pendingSecondCallCalls = Number(period.pendingSecondCallCalls || 0);
  const callsConverted = enrolledCalls + pendingReferralCalls + pendingSecondCallCalls;
  const exclusionOverlapAdjustment =
    convertiblePatients +
    noShowCalls +
    notEligibleCalls +
    (period.remainingCalls == null ? lastMinuteRescheduleCalls : 0) -
    remainingCalls;
  return {
    totalBookings,
    remainingCalls,
    nonCancelledCalls: remainingCalls,
    noShowCalls,
    notEligibleCalls,
    lastMinuteRescheduleCalls,
    callsConverted,
    callsNotConverted: convertiblePatients - callsConverted,
    convertiblePatients,
    exclusionOverlapAdjustment,
    noShowRate: safeRate(noShowCalls, remainingCalls),
    notEligibleRate: safeRate(notEligibleCalls, convertiblePatients + notEligibleCalls),
    conversionRateRemainingBookings: safeRate(callsConverted, remainingCalls),
    conversionRateNonCancelledCalls: safeRate(callsConverted, remainingCalls),
    conversionRateConvertiblePatients: safeRate(callsConverted, convertiblePatients),
  };
}

export function formatAbsoluteChange(currentValue, previousValue) {
  const delta = Number(currentValue || 0) - Number(previousValue || 0);
  if (delta > 0) return `+${delta.toLocaleString()}`;
  return delta.toLocaleString();
}

export function changeColor(currentValue, previousValue) {
  const current = Number(currentValue || 0);
  const previous = Number(previousValue || 0);
  if (current > previous) return COLORS.green;
  if (current < previous) return COLORS.red;
  return COLORS.gray;
}

export function nthWeekdayOfMonth(year, monthIndex, weekday, occurrence) {
  const date = new Date(Date.UTC(year, monthIndex, 1));
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() + 1);
  }
  date.setUTCDate(date.getUTCDate() + (occurrence - 1) * 7);
  return date;
}

export function lastWeekdayOfMonth(year, monthIndex, weekday) {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0));
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return date;
}

export function observedDate(year, monthIndex, day) {
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() - 1);
  if (date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function holidayDatesForYear(year) {
  return new Set([
    observedDate(year, 0, 1),
    nthWeekdayOfMonth(year, 0, 1, 3).toISOString().slice(0, 10),
    nthWeekdayOfMonth(year, 1, 1, 3).toISOString().slice(0, 10),
    lastWeekdayOfMonth(year, 4, 1).toISOString().slice(0, 10),
    observedDate(year, 5, 19),
    observedDate(year, 6, 4),
    nthWeekdayOfMonth(year, 8, 1, 1).toISOString().slice(0, 10),
    nthWeekdayOfMonth(year, 9, 1, 2).toISOString().slice(0, 10),
    observedDate(year, 10, 11),
    nthWeekdayOfMonth(year, 10, 4, 4).toISOString().slice(0, 10),
    observedDate(year, 11, 25),
  ]);
}

export function isWeekendOrHoliday(dateIso) {
  const date = new Date(`${dateIso}T00:00:00Z`);
  const day = date.getUTCDay();
  if (day === 0 || day === 6) return true;
  return holidayDatesForYear(date.getUTCFullYear()).has(dateIso);
}

export function completedMonthOptions(baseDateIso, count = 6) {
  const [year, month] = baseDateIso.split("-").map(Number);
  const options = [];
  for (let offset = 1; offset <= count; offset += 1) {
    const monthIndex = month - 1 - offset;
    const date = new Date(Date.UTC(year, monthIndex, 1));
    const snapshotDate = monthEndIso(date.getUTCFullYear(), date.getUTCMonth());
    options.push({
      value: snapshotDate,
      label: monthLabel(snapshotDate),
    });
  }
  return options;
}

export function aggregateMonthlyRows(rows, selectedClinics, primaryKey, secondaryKey) {
  const selected = selectedClinics == null ? null : new Set(selectedClinics);
  const bucketMap = new Map();

  rows.forEach((row) => {
    if (selected && !selected.has(row.clinicName)) return;
    const current = bucketMap.get(row.month) || {
      month: row.month,
      primary: 0,
      secondary: 0,
      total: 0,
    };
    current.primary += Number(row[primaryKey] || 0);
    current.secondary += Number(row[secondaryKey] || 0);
    current.total = current.primary + current.secondary;
    bucketMap.set(row.month, current);
  });

  return [...bucketMap.values()].sort((a, b) => a.month.localeCompare(b.month));
}

export const ALIGNMENT_LAG_BUCKETS = [
  { key: "aligned1To14Days", label: "≤14 days", color: COLORS.green },
  { key: "aligned15To30Days", label: "15-30 days", color: COLORS.blue },
  { key: "aligned30To60Days", label: "30-60 days", color: COLORS.yellow },
  { key: "aligned60To90Days", label: "60-90 days", color: COLORS.orange },
  { key: "aligned90PlusDays", label: "90+ days", color: COLORS.red },
];

export function emptyAlignmentLagRow(month) {
  const row = { month, total: 0 };
  ALIGNMENT_LAG_BUCKETS.forEach((bucket) => {
    row[bucket.key] = 0;
  });
  return row;
}

export function aggregateAlignmentLagRows(rows, selectedClinics) {
  const selected = selectedClinics == null ? null : new Set(selectedClinics);
  const bucketMap = new Map();

  rows.forEach((row) => {
    if (selected && !selected.has(row.clinicName)) return;
    const current = bucketMap.get(row.month) || emptyAlignmentLagRow(row.month);

    ALIGNMENT_LAG_BUCKETS.forEach((bucket) => {
      current[bucket.key] += Number(row[bucket.key] || 0);
    });
    current.total = ALIGNMENT_LAG_BUCKETS.reduce((sum, bucket) => sum + current[bucket.key], 0);
    bucketMap.set(row.month, current);
  });

  return [...bucketMap.values()].sort((a, b) => a.month.localeCompare(b.month));
}

export function aggregateNeuroAppointmentRows(rows, selectedClinicIds, anchorDate) {
  const selected = selectedClinicIds == null ? null : new Set(selectedClinicIds);
  const countsByDate = new Map();

  rows.forEach((row) => {
    if (selected && !selected.has(row.clinicId)) return;
    const current = countsByDate.get(row.date) || {
      withRetellCallCount: 0,
      withoutRetellCallCount: 0,
    };
    const key = row.hasRetellCall ? "withRetellCallCount" : "withoutRetellCallCount";
    current[key] += Number(row.carespaceCount || 0);
    countsByDate.set(row.date, current);
  });

  return Array.from({ length: 15 }, (_, index) => {
    const date = shiftIsoDate(anchorDate, index - 7, "day");
    return {
      date,
      withRetellCallCount: countsByDate.get(date)?.withRetellCallCount || 0,
      withoutRetellCallCount: countsByDate.get(date)?.withoutRetellCallCount || 0,
    };
  });
}

export function weekStartIso(dateIso) {
  const date = new Date(`${dateIso}T00:00:00Z`);
  const daysSinceMonday = (date.getUTCDay() + 6) % 7;
  return shiftIsoDate(dateIso, -daysSinceMonday, "day");
}

export function summarizeNeuroAppointmentWeeks(rows, selectedClinicIds, anchorDate) {
  const selected = selectedClinicIds == null ? null : new Set(selectedClinicIds);
  const currentWeekStart = weekStartIso(anchorDate);
  const currentWeekEnd = shiftIsoDate(currentWeekStart, 6, "day");
  const priorWeekStart = shiftIsoDate(currentWeekStart, -7, "day");
  const priorWeekEnd = shiftIsoDate(currentWeekStart, -1, "day");
  let current = 0;
  let prior = 0;

  rows.forEach((row) => {
    if (selected && !selected.has(row.clinicId)) return;
    // Summing the SQL's distinct carespace count for each visit date makes
    // this weekly total reconcile exactly with the chart's daily bars.
    const count = Number(row.carespaceCount || 0);
    if (row.date >= currentWeekStart && row.date <= currentWeekEnd) {
      current += count;
    } else if (row.date >= priorWeekStart && row.date <= priorWeekEnd) {
      prior += count;
    }
  });

  return { current, prior, delta: current - prior };
}

export const BOOKING_SOURCE_SERIES = [
  { key: "es", label: "Scheduled by ES", color: COLORS.blue },
  { key: "ai", label: "Scheduled by AI", color: COLORS.purple },
  { key: "web", label: "Scheduled via web", color: COLORS.orange },
  { key: "unclassified", label: "Unclassified", color: COLORS.gray },
];

export function aggregateBookingSourceRows(rows, selectedClinicIds, anchorDate, isCancelled) {
  const selected = selectedClinicIds == null ? null : new Set(selectedClinicIds);
  const currentWeekStart = weekStartIso(anchorDate);
  const weeks = Array.from({ length: 8 }, (_, index) => {
    const weekStart = shiftIsoDate(currentWeekStart, (index - 7) * 7, "day");
    return {
      weekStart,
      weekEnd: shiftIsoDate(weekStart, 6, "day"),
      es: 0,
      ai: 0,
      web: 0,
      unclassified: 0,
    };
  });
  const weekMap = new Map(weeks.map((week) => [week.weekStart, week]));

  rows.forEach((row) => {
    if (selected && !selected.has(row.clinicId)) return;
    if (row.isCancelled !== isCancelled) return;
    const week = weekMap.get(row.weekStart);
    if (!week || !BOOKING_SOURCE_SERIES.some(({ key }) => key === row.schedulingSource)) {
      return;
    }
    week[row.schedulingSource] += Number(row.bookingCount || 0);
  });

  return weeks;
}
